import { useState } from "react";
import { Power, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { LifecycleBadge, RiskBadge, KillSwitchBadge, ReadinessBar } from "../Badges";
import { Modal } from "./shared";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { can, DISABLED_BTN_CLS, permissionTitle } from "../../lib/permissions";
import { toast } from "sonner";

const RECERT_SCOPES = [
    { id: "quick", label: "Quick", help: "Reset only Gate 10 (Mission Ready Release / Monitoring). Fastest path to clearance." },
    { id: "full", label: "Full", help: "Reset every gate in the current control mode back to Not Started." },
    { id: "critical_track", label: "Critical Track", help: "Switch to the Critical Systems Track and rebuild all 11 gates." },
];

export default function CommandHeader({ product, reliability, onChange }) {
    const { user } = useAuth();
    const canKill = can(user, "killSwitch");
    const canRecert = can(user, "initiateRecert");
    const [modalOpen, setModalOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Recert modal
    const [recertOpen, setRecertOpen] = useState(false);
    const [recertScope, setRecertScope] = useState("quick");
    const [recertReason, setRecertReason] = useState("");
    const [recertBusy, setRecertBusy] = useState(false);
    const [recertErr, setRecertErr] = useState("");

    if (!product) return null;

    const submit = async () => {
        const trimmed = reason.trim();
        if (trimmed.length < 3) {
            setError("Reason is required (min 3 characters).");
            return;
        }
        setSubmitting(true);
        setError("");
        const next = !product.kill_switch_active;
        try {
            await api.post(`/products/${product.id}/kill-switch`, { active: next, reason: trimmed });
            toast.success(next ? "Kill switch ARMED" : "Kill switch released");
            setModalOpen(false);
            setReason("");
            onChange && onChange();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const close = () => {
        setModalOpen(false);
        setReason("");
        setError("");
    };

    const submitRecert = async () => {
        const trimmed = recertReason.trim();
        if (trimmed.length < 3) {
            setRecertErr("Reason is required (min 3 characters).");
            return;
        }
        setRecertBusy(true);
        setRecertErr("");
        try {
            await api.post(`/products/${product.id}/recertify`, { scope: recertScope, reason: trimmed });
            toast.success(`Recertification started (${recertScope}). Kill switch is engaged until gates pass.`);
            setRecertOpen(false);
            setRecertReason("");
            setRecertScope("quick");
            onChange && onChange();
        } catch (err) {
            setRecertErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setRecertBusy(false);
        }
    };

    const score = product.readiness_score ?? 0;
    const scoreColor = score >= 80 ? "#FF6A00" : score >= 50 ? "#FFC107" : "#FF3B30";

    const reliabilityScore = reliability?.reliability_score ?? null;
    const reliabilityColor = reliabilityScore == null
        ? "#A0A0A0"
        : reliabilityScore >= 80 ? "#4CAF50"
        : reliabilityScore >= 50 ? "#FFC107"
        : "#FF6A00";

    const recertEligible =
        canRecert &&
        !product.active_recertification &&
        (product.lifecycle_status === "Suspended" ||
         product.kill_switch_active ||
         (reliability?.unresolved ?? 0) > 0);

    return (
        <div
            data-testid="command-header"
            className={`border ${product.kill_switch_active ? "border-signal" : "border-hairline"} bg-jet-elev1 p-6`}
        >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1 min-w-0">
                    <div className="overline">Command Center</div>
                    <h1
                        className="heading-display text-4xl mt-1 truncate"
                        data-testid="product-name-heading"
                    >
                        {product.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <LifecycleBadge status={product.lifecycle_status} />
                        <RiskBadge tier={product.risk_tier} />
                        <KillSwitchBadge active={product.kill_switch_active} size="lg" />
                        <span className="overline">{product.product_type}</span>
                        {product.active_recertification && (
                            <span
                                data-testid="recert-active-badge"
                                className="inline-flex items-center gap-1 border border-signal bg-signal/10 text-signal px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em] animate-pulse-signal"
                            >
                                <ShieldCheck className="w-3 h-3" /> Recert {product.recertification_scope || ""}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
                    <div className="border border-hairline px-5 py-3 min-w-[200px]" data-testid="readiness-widget">
                        <div className="overline">Readiness</div>
                        <div className="flex items-baseline gap-1 mt-1">
                            <div
                                className="heading-display text-5xl tabular-nums"
                                style={{ color: scoreColor }}
                                data-testid="readiness-score-value"
                            >
                                {score}
                            </div>
                            <div className="text-snow-muted text-sm font-mono">/100</div>
                        </div>
                        <div className="mt-2">
                            <ReadinessBar value={score} />
                        </div>
                        <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                            Auto = passed / total gates
                        </div>
                    </div>

                    <div
                        className="border border-[#2A2A2A] px-5 py-3 min-w-[180px]"
                        data-testid="reliability-widget"
                    >
                        <div className="overline">Reliability</div>
                        <div className="flex items-baseline gap-1 mt-1">
                            <div
                                className="heading-display text-5xl tabular-nums"
                                style={{ color: reliabilityColor }}
                                data-testid="reliability-score-value"
                            >
                                {reliabilityScore ?? "—"}
                            </div>
                            <div className="text-snow-muted text-sm font-mono">/100</div>
                        </div>
                        <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                            From field reports • {reliability?.total_reports ?? 0} total
                            {reliability?.unresolved ? ` • ${reliability.unresolved} open` : ""}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <button
                            data-testid="header-kill-switch-btn"
                            onClick={() => canKill && setModalOpen(true)}
                            disabled={!canKill}
                            title={permissionTitle(user, "killSwitch")}
                            className={`border px-5 py-3 transition-colors flex items-center gap-3 min-w-[200px] ${
                                !canKill
                                    ? DISABLED_BTN_CLS
                                    : product.kill_switch_active
                                    ? "border-signal bg-signal/10 text-signal hover:bg-signal hover:text-jet"
                                    : "border-hairline text-snow-dim hover:border-signal hover:text-signal"
                            }`}
                        >
                            <Power className={`w-5 h-5 ${product.kill_switch_active && canKill ? "animate-pulse-signal" : ""}`} />
                            <div className="text-left">
                                <div className="overline text-[9px]">Kill Switch</div>
                                <div className="font-bold uppercase tracking-wider text-sm mt-0.5">
                                    {!canKill ? "Restricted" : product.kill_switch_active ? "Release" : "Engage"}
                                </div>
                            </div>
                        </button>
                        <button
                            data-testid="initiate-recert-btn"
                            onClick={() => recertEligible && setRecertOpen(true)}
                            disabled={!recertEligible}
                            title={
                                !canRecert
                                    ? permissionTitle(user, "initiateRecert")
                                    : product.active_recertification
                                    ? "Recertification already active"
                                    : recertEligible
                                    ? "Reset gates to validate recovery"
                                    : "Available when product is Suspended, kill-switched, or has open reports"
                            }
                            className={`border px-5 py-3 transition-colors flex items-center gap-3 min-w-[200px] ${
                                !recertEligible
                                    ? DISABLED_BTN_CLS
                                    : "border-hairline text-snow-dim hover:border-signal hover:text-signal"
                            }`}
                        >
                            <ShieldCheck className="w-5 h-5" />
                            <div className="text-left">
                                <div className="overline text-[9px]">Recertify</div>
                                <div className="font-bold uppercase tracking-wider text-sm mt-0.5">
                                    {product.active_recertification ? "In Progress" : "Initiate"}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <Modal title="Kill Switch Confirmation" onClose={close} accent>
                    <div className="space-y-4" data-testid="detail-kill-modal">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-signal flex-shrink-0 mt-1" />
                            <div className="flex-1">
                                <div className="heading-display text-lg">
                                    {product.kill_switch_active ? "Release kill switch?" : "Engage kill switch?"}
                                </div>
                                <div className="text-sm text-snow-dim mt-2 font-mono">
                                    Target: <span className="text-snow">{product.name}</span>
                                </div>
                                <div className="text-xs text-snow-muted mt-2">
                                    {product.kill_switch_active
                                        ? (product.active_recertification
                                            ? "Release will only succeed after every recertification gate is Passed."
                                            : "Releasing will remove emergency lockout and restore operational mode.")
                                        : "Engaging will trigger an immediate emergency lockout. This action is logged."}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">
                                Reason <span className="text-signal">*</span>
                            </label>
                            <textarea
                                data-testid="detail-kill-reason-input"
                                rows={3}
                                autoFocus
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="cerb-input"
                                maxLength={500}
                                placeholder="Reason will be written to audit_logs"
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                <span>Required • will be logged</span>
                                <span className="tabular-nums">{reason.length}/500</span>
                            </div>
                        </div>
                        {error && (
                            <div className="border border-red-500/40 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono" data-testid="detail-kill-error">
                                {error}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={close} className="cerb-btn-secondary">Cancel</button>
                            <button
                                onClick={submit}
                                disabled={submitting || reason.trim().length < 3}
                                className="cerb-btn-primary"
                                data-testid="detail-kill-confirm-btn"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (product.kill_switch_active ? "Release" : "Engage")}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {recertOpen && (
                <Modal title="Initiate Recertification" onClose={() => setRecertOpen(false)} accent>
                    <div className="space-y-4" data-testid="recert-modal">
                        <div className="flex items-start gap-3">
                            <ShieldCheck className="w-6 h-6 text-signal flex-shrink-0 mt-1" />
                            <div className="flex-1">
                                <div className="heading-display text-lg">Reset gates to validate recovery?</div>
                                <div className="text-sm text-snow-dim mt-2 font-mono">
                                    Target: <span className="text-snow">{product.name}</span>
                                </div>
                                <div className="text-xs text-snow-muted mt-2">
                                    The kill switch will <span className="text-signal">stay engaged</span> until every selected gate reaches Passed.
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2" data-testid="recert-scope-radios">
                            {RECERT_SCOPES.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    data-testid={`recert-scope-${s.id}`}
                                    onClick={() => setRecertScope(s.id)}
                                    className={`w-full text-left border px-3 py-2 transition-colors ${
                                        recertScope === s.id
                                            ? "border-signal bg-signal/10"
                                            : "border-[#2A2A2A] hover:border-snow"
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={`w-3 h-3 rounded-full border ${recertScope === s.id ? "bg-signal border-signal" : "border-snow-muted"}`}
                                        />
                                        <span className={`font-mono text-sm uppercase tracking-wider ${recertScope === s.id ? "text-signal" : "text-snow"}`}>{s.label}</span>
                                    </div>
                                    <div className="text-[11px] text-snow-dim mt-1 font-mono pl-5">{s.help}</div>
                                </button>
                            ))}
                        </div>
                        <div>
                            <label className="overline block mb-2">Reason <span className="text-signal">*</span></label>
                            <textarea
                                data-testid="recert-reason-input"
                                rows={3}
                                value={recertReason}
                                onChange={(e) => setRecertReason(e.target.value)}
                                className="cerb-input"
                                maxLength={500}
                                placeholder="e.g. FR-0007 fix verified in lab; running G10 monitoring."
                            />
                        </div>
                        {recertErr && <div className="text-red-400 text-xs font-mono">{recertErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setRecertOpen(false)} className="cerb-btn-secondary" data-testid="recert-cancel-btn">Cancel</button>
                            <button
                                onClick={submitRecert}
                                disabled={recertBusy || recertReason.trim().length < 3}
                                className="cerb-btn-primary"
                                data-testid="recert-confirm-btn"
                            >
                                {recertBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Initiate"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}
